// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Generate data image url of a given deck shape.
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import { Application, Color, Container, Graphics, Matrix, Point, Text } from "pixi.js"

// @@imports-types
import { DeckOption } from "../../../../package/types/index.js"
import { Bom, Deck, Edge, Rectangle, Vector2D } from "../../../../package/index.js"

// @@body
// Declare module-scoped global variables.
const APP = new Application()
let RENDERING = false
const RENDER_QUEUE = []

/**
 * add options object, and add options for fill, shadow, shadow width, shadow
 * height, shadow step 
 * @param {DeckOption|Deck} deckOption
 * @param {object} obj Optional destructured parameters object.
 * @param {number} [obj.imageSize]
 * @param {number} [obj.fontSize]
 * @param {number} [obj.rotation]
 * @param {number} [obj.skew]
 * @param {number} [obj.fillColor]
 * @param {number} [obj.strokeColor]
 * @param {number} [obj.strokeWidth]
 * @param {number} [obj.shadowWidth]
 * @param {?Edge} [obj.selectedEdge]
 * @param {number} [obj.selectedStrokeWidth]
 * @param {Map.<string, {fractions:Array.<{fraction:number, id:string}>, flipped:boolean}>} [obj.zones]
 * @param {boolean} [obj.renderEdgeLabels]
 * @param {boolean} [obj.forceRender]
 * @returns {Promise.<string>}
 */
const getDeckImageUrl = async (deckOption, {
    imageSize = 1024,
    fontSize = 96,
    rotation = Math.PI / 4,
    skew = 0.5,
    fillColor = 0xbfbfbf,
    strokeColor = 0x212121,
    strokeWidth = 16,
    shadowWidth = 32,
    selectedEdge = null,
    selectedStrokeWidth = 64,
    zones = new Map(),
    renderEdgeLabels = false,
    forceEdgeLabelPadding = false,
    labelEdgeOffset = 48,
    labelEndOffset = 48,
    labelCrossBarWidth = 64,
    forceRender = false
} = {}) => {
    if (RENDERING) {
        return await new Promise(resolve => {
            RENDER_QUEUE.push(async () => {
                const dataUrl = await getDeckImageUrl(deckOption, {
                    imageSize, fontSize, rotation, skew, fillColor, strokeColor,
                    strokeWidth, selectedEdge, selectedStrokeWidth, zones,
                    renderEdgeLabels, forceEdgeLabelPadding, labelEdgeOffset,
                    labelEndOffset, labelCrossBarWidth, forceRender
                })
                resolve(dataUrl)
            })
        })
    }

    // 
    RENDERING = true

    // Fetch and return deck image if it is already set to prevent re-rendering
    // when the deck is unchanged.
    const { deck, imageUrl } = deckOption instanceof Deck
        ? { deck: deckOption, imageUrl: null } : deckOption
    if (imageUrl && !forceRender) { return imageUrl }

    if (!APP.renderer) {
        await APP.init({
            width: imageSize,
            height: imageSize,
            resolution: window.devicePixelRatio,
            autoDensity: true,
            backgroundAlpha: 0
        })
    }
    else {
        APP.start()
        APP.renderer.resize(imageSize, imageSize)
    }

    const { width: deckWidth, height: deckHeight } = deck.boundingBox
    let scale = imageSize / Math.max(deckWidth, deckHeight)
    const deckVertices = deck.vertices.array.map(vertex => {
        return new Point(vertex.x * scale, vertex.y * scale)
    })

    for (let i = 1; i < shadowWidth; i += 0.125) {
        const shadowGraphic = new Graphics()
            .rotateTransform(rotation)
            .transform(new Matrix(1, - skew, 0, 1, i, i))
            .poly(deckVertices)
            .fill(strokeColor)
            .stroke({
                color: strokeColor,
                width: strokeWidth
            })
        APP.stage.addChild(shadowGraphic)
    }

    const deckGraphic = new Graphics()
        .rotateTransform(rotation)
        .transform(new Matrix(1, - skew))
        .poly(deckVertices)
        .fill(fillColor)
        .stroke({
            color: selectedEdge ? fillColor : strokeColor,
            width: strokeWidth
        })
    APP.stage.addChild(deckGraphic)

    if (renderEdgeLabels || forceEdgeLabelPadding) {
        const transparent = new Color("0x00000000")

        for (const [index, edge] of deck.edges.array.entries()) {
            const container = new Container()
            const graphic = new Graphics()
            const crossBars = new Graphics()
            const text = new Text({
                text: `${index + 1}`,
                style: {
                    fontSize,
                    fill: renderEdgeLabels ? strokeColor : transparent
                }
            })
            container.addChild(graphic, crossBars, text)

            const { vertexOrientation: orientation } = deck
            const offsetWidthRatio = labelEdgeOffset / strokeWidth
            const edgeAlignment = orientation * (offsetWidthRatio + 0.5)
            const measurementLength = edge.magnitude * scale

            graphic
                .moveTo(labelEndOffset, 0)
                .lineTo(measurementLength - labelEndOffset, 0)
                .stroke({
                    color: renderEdgeLabels ? strokeColor : transparent,
                    width: strokeWidth,
                    alignment: edgeAlignment
                })

            const barAlignment = orientation * labelEdgeOffset
            const crossBarStart = labelCrossBarWidth / 2 - barAlignment
            const crossBarEnd = - (barAlignment + labelCrossBarWidth / 2)
            crossBars
                .moveTo(labelEndOffset, crossBarStart)
                .lineTo(labelEndOffset, crossBarEnd)
                .stroke({
                    color: renderEdgeLabels ? strokeColor : transparent,
                    width: strokeWidth
                })
                .moveTo(measurementLength - labelEndOffset, crossBarStart)
                .lineTo(measurementLength - labelEndOffset, crossBarEnd)
                .stroke({
                    color: renderEdgeLabels ? strokeColor : transparent,
                    width: strokeWidth
                })

            container.rotation = edge.direction
            container.x = edge.start.x * scale
            container.y = edge.start.y * scale

            text.x = (measurementLength - text.width) / 2
            text.y = orientation === 1
                ? - (labelEdgeOffset + text.height)
                : labelEdgeOffset

            if (
                orientation === 1 && container.rotation === Math.PI ||
                orientation === - 1 && container.rotation !== 0
            ) {
                text.rotation = Math.PI
                text.x += text.width
                text.y += text.height
            }

            APP.stage.addChild(container)
        }
    }

    if (selectedEdge) {
        const selectedEdgeGraphic = new Graphics()
            .rotateTransform(rotation)
            .transform(new Matrix(1, - skew))
            .moveTo(selectedEdge.start.x * scale, selectedEdge.start.y * scale)
            .lineTo(selectedEdge.end.x * scale, selectedEdge.end.y * scale)
            .stroke({
                color: strokeColor,
                width: selectedStrokeWidth
            })
        APP.stage.addChild(selectedEdgeGraphic)
    }

    for (const [serializedZone, { fractions, flipped }] of zones) {
        const zone = Rectangle.fromSerialized(serializedZone)
        let start = 0
        for (const { fraction, id } of fractions) {
            const end = start + fraction
            const subZone = zone.getSubRectangle({ start, end, flipped })

            const subZoneVertices = subZone.vertices.array.map(vertex => {
                return new Point(vertex.x * scale, vertex.y * scale)
            })

            const subZoneGraphic = new Graphics()
                .rotateTransform(rotation)
                .transform(new Matrix(1, - skew))
                .poly(subZoneVertices)
                .fill(fillColor)
                .stroke({
                    color: selectedEdge ? fillColor : strokeColor,
                    width: strokeWidth
                })

            const { width, height, anchor: { x, y } } = subZone
            const text = new Text({ text: id, style: { fontSize } })
            text.x = (x + width / 2) * scale - text.width / 2
            text.y = (y + height / 2) * scale - text.height / 2

            APP.stage.addChild(subZoneGraphic)
            APP.stage.addChild(text)

            start = end
        }
    }

    APP.stage.updateTransform({ x: 0, y: 0, scaleX: 1, scaleY: 1 })

    let { minX, minY, maxX, maxY } = APP.stage.getBounds()
    scale = imageSize / Math.max(maxX - minX, maxY - minY)
    APP.stage.updateTransform({ scaleX: scale, scaleY: scale });

    ({ minX, minY, maxX, maxY } = APP.stage.getBounds())
    APP.stage.updateTransform({
        x: - minX + (imageSize - (maxX - minX)) / 2,
        y: - minY + (imageSize - (maxY - minY)) / 2
    })

    APP.render()
    const dataUrl = APP.canvas.toDataURL()

    // Purge all children from APPlication stage, and destroy their resources.
    while (APP.stage.children.length) {
        const child = APP.stage.children[0]
        APP.stage.removeChild(child)
        child.destroy({ children: true })
    }

    // Resize renderer and stop APP to save on memory and compute resources. Do
    // not destroy APPlication, as this causes loss of WebGL context warning.
    // Repeated loss of WebGL contexts can cause some browsers to force reload
    // or crash the page (such as ios safari).
    APP.renderer.clear({ clear: true, clearColor: 0xffffff })
    APP.renderer.resize(1, 1)
    APP.stop()

    // 
    RENDERING = false
    if (RENDER_QUEUE.length) { RENDER_QUEUE.shift()() }

    return dataUrl
}

/**
 * 
 * @param {Bom} bom 
 * @param {Deck} deck 
 * @returns 
 */
const getMembraneBreakdownImageUrl = async (bom, deck) => {
    const zones = new Map()
    for (const { detail } of bom.filterDisplayCategory("Membranes").lineItems) {
        if (!detail) { continue }
        const zone = zones.get(detail.zone)
        if (zone) {
            const { fraction, id } = detail
            zone.fractions.push({ fraction, id })
        }
        else {
            const { fraction, flipped, id } = detail
            const fractions = [{ fraction, id }]
            zones.set(detail.zone, { fractions, flipped })
        }
    }

    const membraneBreakdownImageUrl = await getDeckImageUrl(deck, {
        rotation: 0,
        skew: 0,
        shadowWidth: 0,
        forceRender: true,
        zones
    })

    return membraneBreakdownImageUrl
}

// @@exports
export { getDeckImageUrl, getMembraneBreakdownImageUrl }
